import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { catchError, finalize, timeout } from 'rxjs/operators';
import { of } from 'rxjs';
import { AppIconComponent } from '@app/shared/icons/app-icon.component';
import { environment } from '@env/environment';

interface AgentProfile {
  name: string;
  description: string;
  'img-url': string;
  email: string;
  phone: string;
  slug?: string;
}

interface AgentsResponse {
  founder?: AgentProfile | null;
  agents?: AgentProfile[];
}

@Component({
  standalone: true,
  imports: [CommonModule, AppIconComponent, RouterLink],
  selector: 'app-agents',
  templateUrl: './agents.component.html',
  styleUrls: ['./agents.component.css']
})
export class AgentsComponent implements OnInit {

  readonly founder = signal<AgentProfile | null>(null);
  readonly agents = signal<AgentProfile[]>([]);
  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly hasAgents = computed(() => !!this.founder() || this.agents().length > 0);

  constructor(private http: HttpClient) { }

  ngOnInit(): void {
    this.http.get<AgentsResponse>(environment.API_URL + 'agents')
      .pipe(
        timeout(15000),
        catchError(() => {
          this.loadError.set(true);
          return of(null);
        }),
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe((response) => {
        if (!response) {
          return;
        }

        this.founder.set(this.normalizeAgent(response.founder));
        this.agents.set(
          (Array.isArray(response.agents) ? response.agents : [])
            .map((agent) => this.normalizeAgent(agent))
            .filter((agent): agent is AgentProfile => agent !== null)
        );
      });
  }

  private normalizeAgent(agent: AgentProfile | null | undefined): AgentProfile | null {
    if (!agent?.name) {
      return null;
    }

    return {
      name: agent.name,
      description: agent.description ?? '',
      'img-url': agent['img-url'] ?? '',
      email: agent.email ?? '',
      phone: agent.phone ?? '',
      slug: agent.slug?.trim() || this.slugifyName(agent.name),
    };
  }

  private slugifyName(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'agent';
  }

}

